import { cn } from '@app/utils/cn';
import { useGalleryDots } from '@app/utils/hooks/useGalleryDots';
import { AnyMedia } from '@typings';
import React from 'react';

import { useProductDetailsLayoutContext } from '../context/ProductDetailsLayoutContext';
import { useProductDetailsVariantContext } from '../context/ProductDetailsVariantContext';

interface Props {
  activeMediaIndex: number;
  medias: AnyMedia[];
}

const DOT_SIZE_BY_OFFSET: Record<number, string> = {
  0: 'large',
  1: 'medium',
  2: 'small',
};

const dotWidth = 16;
const visibleDotsCount = 7;

export const ProductDetailsGalleryDots = ({ activeMediaIndex, medias }: Props) => {
  const { handleMediaIndexChange } = useProductDetailsVariantContext();
  const { isTablet, isDesktop } = useProductDetailsLayoutContext();

  const activeDotRef = React.useRef<HTMLButtonElement>(null);

  const { dotsStyles, wrapperRef } = useGalleryDots({
    activeIndex: activeMediaIndex,
    dotSize: dotWidth,
    onChange: handleMediaIndexChange,
    orientation: isDesktop ? 'vertical' : 'horizontal',
    totalCount: medias.length,
    visibleCount: visibleDotsCount,
  });

  const dots = React.useMemo(() => {
    return medias
      .map((_, index) => ({
        size: DOT_SIZE_BY_OFFSET[Math.min(2, Math.abs(index - activeMediaIndex))] ?? 'large',
      }))
      .slice(0, isTablet ? -1 : medias.length);
  }, [activeMediaIndex, isTablet, medias]);

  if (dots.length <= 1) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute bottom-0 left-1/2 z-[3] -translate-x-1/2 translate-y-1.5 rounded-tl-large rounded-tr-large bg-white px-3 py-0.5',
        'lg:bottom-auto lg:left-auto lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-2 lg:rounded-none lg:rounded-bl-large lg:rounded-tl-large lg:p-0 lg:px-1 lg:py-4',
      )}
    >
      <div ref={wrapperRef} className="h-full w-full overflow-hidden">
        <div role="radiogroup" style={dotsStyles} className="flex transition-transform duration-200 lg:flex-col">
          {dots.map(({ size }, index) => (
            <button
              ref={activeMediaIndex === index ? activeDotRef : undefined}
              type="button"
              role="radio"
              aria-checked={activeMediaIndex === index}
              aria-label={`Media ${index + 1}`}
              key={index}
              tabIndex={activeMediaIndex === index ? 0 : -1}
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full !-outline-offset-2"
              onClick={() => handleMediaIndexChange(index)}
            >
              <div
                className={cn('block aspect-square size-1 rounded-full bg-neutral-80 shadow-2 transition-all duration-200', {
                  'size-1.5 bg-neutral-60': size === 'medium',
                  'size-2 bg-neutral-40': size === 'large',
                })}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
